import React from 'react';
import { Check, X, Code2, Plug, Workflow, Layout, Database, Terminal, Settings, Braces, Layers, Key, Star } from 'lucide-react';

const Integrations: React.FC = () => {
  const serviceAreas = [
    {
      icon: <Plug size={32} />,
      title: "Conexión vía API/Web Services",
      description: "Desarrollamos interfaces para conectar tu LMS con ERPs académicos (Banner, PeopleSoft), sistemas de pago y CRMs mediante REST/SOAP.",
      benefit: "Sincronización automática de matrículas, notas y pagos en tiempo real."
    },
    {
      icon: <Code2 size={32} />,
      title: "Desarrollo de Plugins a Medida",
      description: "Creamos funcionalidades específicas que Moodle no trae por defecto: nuevos tipos de preguntas, formatos de curso o módulos de asistencia personalizados.",
      benefit: "Extender las capacidades de tu plataforma para que se adapte 100% a tu metodología."
    },
    {
      icon: <Key size={32} />,
      title: "Single Sign-On (SSO)",
      description: "Implementación de autenticación unificada con Google Workspace, Microsoft 365, LDAP o CAS para acceso con una sola credencial.",
      benefit: "Mejor experiencia de usuario y mayor seguridad en el acceso."
    },
    {
      icon: <Layout size={32} />,
      title: "Temas Visuales Personalizados",
      description: "Diseño y maquetación de interfaces gráficas únicas que respetan estrictamente el manual de marca de tu institución.",
      benefit: "Identidad visual coherente y navegación intuitiva para el alumno."
    },
    {
      icon: <Database size={32} />,
      title: "Migración y Limpieza de Datos",
      description: "Scripts ETL para cargas masivas de usuarios, cursos o históricos desde sistemas legados o archivos planos complejos.",
      benefit: "Manejo eficiente de grandes volúmenes de información."
    },
    {
      icon: <Workflow size={32} />,
      title: "Automatización de Certificados",
      description: "Sistemas para generación, validación QR y envío automático de certificados y diplomas al completar cursos.",
      benefit: "Reducción drástica de carga administrativa en departamentos de certificación."
    }
  ];

  const plans = [
    {
      name: "Pack Integración",
      price: "Desde $650.000",
      period: "/pago único",
      description: "Conexiones estándar",
      popular: false,
      features: [
        { text: "Configuración SSO (Google/M365)", included: true },
        { text: "Integración Pasarela Pagos", included: true },
        { text: "Conexión Zoom/Teams", included: true },
        { text: "Pruebas de funcionamiento", included: true },
        { text: "Documentación técnica", included: true },
        { text: "Desarrollo a medida", included: false },
        { text: "Soporte evolutivo", included: false }
      ],
      buttonText: "Consultar Pack",
      buttonClass: "btn-secondary"
    },
    {
      name: "Bolsa de Horas",
      price: "$1.200.000",
      period: "/pack 20 horas",
      description: "Desarrollo flexible",
      popular: true,
      features: [
        { text: "Desarrolladores Senior PHP/JS", included: true },
        { text: "Consultoría de arquitectura", included: true },
        { text: "Desarrollo de plugins simples", included: true },
        { text: "Ajustes de tema visual (CSS/JS)", included: true },
        { text: "Consultas SQL a medida", included: true },
        { text: "Vigencia de 6 meses", included: true },
        { text: "Reporte de consumo detallado", included: true },
        { text: "Gerencia de proyecto", included: false }
      ],
      buttonText: "Contratar Bolsa",
      buttonClass: "btn-primary"
    },
    {
      name: "Proyecto a Medida",
      price: "A cotizar",
      period: "/proyecto",
      description: "Desarrollos complejos",
      popular: false,
      features: [
        { text: "Levantamiento de requerimientos", included: true },
        { text: "Diseño de arquitectura de software", included: true },
        { text: "Desarrollo Agile (Sprints)", included: true },
        { text: "QA y Testing exhaustivo", included: true },
        { text: "Paso a producción asistido", included: true },
        { text: "Garantía de 6 meses", included: true },
        { text: "Manuales de usuario", included: true },
        { text: "Transferencia tecnológica", included: true }
      ],
      buttonText: "Cotizar Proyecto",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Terminal size={24} />,
      title: "Código Seguro",
      description: "Desarrollos siguiendo estándares de seguridad de Moodle"
    },
    {
      icon: <Settings size={24} />,
      title: "Automatización",
      description: "Elimina tareas manuales repetitivas propensas a error"
    },
    {
      icon: <Braces size={24} />,
      title: "Mantenibilidad",
      description: "Código documentado y limpio fácil de mantener"
    },
    {
      icon: <Layers size={24} />,
      title: "Escalabilidad",
      description: "Soluciones diseñadas para alto volumen de transacciones"
    }
  ];

  const useCases = [
    {
      title: "Sincronización Académica",
      description: "Universidad integra su sistema de gestión académica con Moodle para crear cursos y matricular alumnos automáticamente cada semestre.",
      results: "0 errores en matrículas, proceso automático"
    },
    {
      title: "Venta de Cursos Online",
      description: "Centro de capacitación conecta WooCommerce con Moodle. Al comprar un curso, el usuario recibe acceso inmediato a la plataforma.",
      results: "Venta 24/7 sin intervención humana"
    },
    {
      title: "Reportes Corporativos",
      description: "Empresa necesita dashboard personalizado para ver avance de capacitación por departamento y exportar a Excel corporativo.",
      results: "Visibilidad total del ROI en capacitación"
    }
  ];

  return (
    <section id="integrations" className="integrations-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Code2 size={16} />
                Desarrollo Expertos
              </div>
              <h1 className="hero-title">
                Integraciones y Desarrollo
                <span className="highlight">A Medida</span>
              </h1>
              <p className="hero-description">
                Conectamos tu ecosistema digital. Desarrollamos plugins, integraciones API y funcionalidades 
                específicas para potenciar tu plataforma educativa.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Consultar Requerimiento
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Plugins Propios</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Compatible Moodle</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">API</div>
                  <div className="stat-label">Rest / SOAP</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Expandiendo las fronteras de tu LMS</h2>
            <p className="section-subtitle">
              Hacemos que tu plataforma hable con el resto de tus sistemas
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Calidad en Desarrollo de Software</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Soluciones Implementadas</h2>
            <p className="section-subtitle">
              Ejemplos reales de cómo la integración transforma procesos
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Modelos de Trabajo</h2>
            <p className="section-subtitle">
              Flexibilidad para proyectos pequeños, medianos y grandes
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Tienes un requerimiento específico?</h3>
            <p>
              Cuéntanos qué necesitas. Nuestro equipo técnico evaluará la factibilidad 
              y te propondrá la mejor solución arquitectónica.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Hablar con un Desarrollador
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Integrations;
